import { useState, useCallback, useMemo } from 'react';
import { BadgeItem, BadgesChangeEvent, BadgesInputValue, TagColor } from '@/shared/types';

interface UseBadgesOptions {
  /** Valor inicial de badges (puede ser BadgeItem[] o string[]) */
  initialValue?: BadgesInputValue;
  defaultColor?: TagColor;
  colorCycle?: TagColor[];
  maxBadges?: number;
  onChange?: (event: BadgesChangeEvent) => void;
  onAdd?: (item: BadgeItem) => void;
  onRemove?: (item: BadgeItem) => void;
}

interface UseBadgesReturn {
  badges: BadgeItem[];
  inputValue: string;
  isFocused: boolean;
  setInputValue: (value: string) => void;
  setIsFocused: (focused: boolean) => void;
  addBadge: (label: string) => void;
  removeBadge: (id: string) => void;
  canAddMore: boolean;
  getNextColor: () => TagColor;
  setBadges: React.Dispatch<React.SetStateAction<BadgeItem[]>>;
}

// Genera un ID único para cada badge
const generateId = (): string => {
  return `badge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Normaliza el valor de entrada a BadgeItem[]
 * Soporta tanto string[] como BadgeItem[]
 */
const normalizeValue = (
  value: BadgesInputValue | undefined,
  defaultColor: TagColor,
  colorCycle?: TagColor[],
): BadgeItem[] => {
  if (!value || value.length === 0) return [];

  // Si el primer elemento es string, convertir todo el array
  if (typeof value[0] === 'string') {
    return (value as string[]).map((label, index) => ({
      id: generateId(),
      label,
      color: colorCycle ? colorCycle[index % colorCycle.length] : defaultColor,
    }));
  }

  // Ya es BadgeItem[]
  return value as BadgeItem[];
};

export const useBadges = ({
  initialValue = [],
  defaultColor = 'accent',
  colorCycle,
  maxBadges,
  onChange,
  onAdd,
  onRemove,
}: UseBadgesOptions): UseBadgesReturn => {
  // Normalizar el valor inicial (solo en mount para no resetear al cambiar props)
  const normalizedInitial = useMemo(
    () => normalizeValue(initialValue, defaultColor, colorCycle),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intención: valor inicial solo al montar
    [],
  );

  // Estado interno de badges - inicializado con initialValue normalizado
  const [badges, setBadges] = useState<BadgeItem[]>(normalizedInitial);
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  // Inicializar colorIndex basado en la cantidad de badges iniciales
  const [colorIndex, setColorIndex] = useState(normalizedInitial.length);

  // Determina si se pueden agregar más badges
  const canAddMore = useMemo(() => {
    if (maxBadges === undefined) return true;
    return badges.length < maxBadges;
  }, [badges.length, maxBadges]);

  // Obtiene el siguiente color del ciclo
  const getNextColor = useCallback((): TagColor => {
    if (!colorCycle || colorCycle.length === 0) {
      return defaultColor;
    }
    return colorCycle[colorIndex % colorCycle.length];
  }, [colorCycle, colorIndex, defaultColor]);

  // Agrega un nuevo badge
  const addBadge = useCallback(
    (label: string) => {
      const trimmedLabel = label.trim();
      if (!trimmedLabel || !canAddMore) return;

      // Evitar duplicados
      const exists = badges.some(
        (badge) => badge.label.toLowerCase() === trimmedLabel.toLowerCase(),
      );
      if (exists) return;

      const newBadge: BadgeItem = {
        id: generateId(),
        label: trimmedLabel,
        color: getNextColor(),
      };

      const newBadges = [...badges, newBadge];
      setBadges(newBadges);
      setInputValue('');

      // Actualizar índice de color para el ciclo
      if (colorCycle && colorCycle.length > 0) {
        setColorIndex((prev) => prev + 1);
      }

      // Callbacks
      onAdd?.(newBadge);
      onChange?.({
        type: 'add',
        item: newBadge,
        items: newBadges,
      });
    },
    [badges, canAddMore, getNextColor, colorCycle, onAdd, onChange],
  );

  // Elimina un badge por ID
  const removeBadge = useCallback(
    (id: string) => {
      const badgeToRemove = badges.find((badge) => badge.id === id);
      if (!badgeToRemove) return;

      const newBadges = badges.filter((badge) => badge.id !== id);
      setBadges(newBadges);

      // Callbacks
      onRemove?.(badgeToRemove);
      onChange?.({
        type: 'remove',
        item: badgeToRemove,
        items: newBadges,
      });
    },
    [badges, onRemove, onChange],
  );

  return {
    badges,
    inputValue,
    isFocused,
    setInputValue,
    setIsFocused,
    addBadge,
    removeBadge,
    canAddMore,
    getNextColor,
    setBadges,
  };
};
